import ModeBadge from '@/components/console/mode-badge';
import StatusPill from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime } from '@/lib/format';
import {
  admin_draft_exception_dismissal_path,
  admin_draft_exception_retry_path,
} from '@/rails/routes';
import type { ProviderMode } from '@/types/platform';
import { router } from '@inertiajs/react';
import { AlertTriangle } from 'lucide-react';

interface DraftException {
  id: string;
  prompt: string;
  problems: string[];
  tier: 'free' | 'paid';
  engine: string | null;
  model: string | null;
  createdAt: string;
  rawOutput: string;
  app: { id: string; name: string };
  company: { name: string; plan: string; exceptionReview: boolean };
}

interface Props {
  exceptions: DraftException[];
  paidModel: string | null;
  engineMode: ProviderMode;
}

const Index = ({ exceptions, paidModel, engineMode }: Props) => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Exceptions"
        description="Drafts that failed their checks. The free model never closes its own exception: retry it on the paid model, or dismiss it."
        badges={<ModeBadge mode={engineMode} />}
      />

      {paidModel ? (
        <p className="text-muted-foreground text-sm">
          Retries run on <code className="text-foreground text-xs">{paidModel}</code>.
        </p>
      ) : (
        <Alert>
          <AlertTriangle className="text-yellow-600" aria-hidden="true" />
          <AlertDescription>
            No paid model is set (OPENROUTER_PAID_MODEL); the retry will fail.
          </AlertDescription>
        </Alert>
      )}

      {exceptions.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState description="No exceptions. Drafts that fail their checks land here." />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {exceptions.map((exception) => (
            <ExceptionCard key={exception.id} exception={exception} />
          ))}
        </div>
      )}
    </div>
  );
};

const ExceptionCard = ({ exception }: { exception: DraftException }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <span>{exception.company.name}</span>
          <StatusPill>{exception.company.plan}</StatusPill>
          {exception.company.exceptionReview ? (
            <StatusPill tone="info">Review included</StatusPill>
          ) : null}
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          {exception.app.name} · {formatDateTime(exception.createdAt)} · {exception.tier} tier
          {exception.engine ? ` · ${exception.engine}` : ''}
          {exception.model ? ` · ${exception.model}` : ''}
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div>
          <h2 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Prompt
          </h2>
          <p className="mt-1 text-sm break-words whitespace-pre-wrap">{exception.prompt}</p>
        </div>

        <div>
          <h2 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Problems
          </h2>
          {exception.problems.length === 0 ? (
            <p className="text-muted-foreground mt-1 text-sm">No problem was recorded.</p>
          ) : (
            <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-red-700">
              {exception.problems.map((problem, index) => (
                <li key={index} className="break-words">
                  {problem}
                </li>
              ))}
            </ul>
          )}
        </div>

        <details className="text-sm">
          <summary className="text-muted-foreground hover:text-foreground cursor-pointer">
            Raw model output
          </summary>
          {exception.rawOutput ? (
            <pre className="bg-muted mt-2 max-h-72 overflow-auto rounded-md p-3 text-xs">
              {exception.rawOutput}
            </pre>
          ) : (
            <p className="text-muted-foreground mt-2">The model returned nothing.</p>
          )}
        </details>
      </CardContent>

      <CardFooter className="flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() => router.post(admin_draft_exception_retry_path(exception.id))}
        >
          Retry on paid model
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => router.post(admin_draft_exception_dismissal_path(exception.id))}
        >
          Dismiss
        </Button>
      </CardFooter>
    </Card>
  );
};

export default Index;
